import React, { useMemo, useState } from "react";
import { Field, TextInput } from "@/components/ReportPrimitives";
import { listByKind, LIB_VERSION } from "@/lib/reference/tubularPickers";

/**
 * TubularLibraryField — input + library-dropdown picker for kick-sheet tubulars.
 *
 * Used only by Kick Sheets (Commit 2). Pure UI — no formulas.
 *
 * Behaviour:
 *  - Renders a TextInput for the raw numeric value the user types/sees.
 *  - "Pick" button opens a list of matching equipment library entries
 *    (filtered by `kind`). Selecting one writes the chosen numeric value
 *    AND a provenance object onto the parent state via `onPick`.
 *  - Manual typing clears the provenance tag and shows a grey "Manual override"
 *    pill instead of the library-provenance pill.
 *  - Provenance pill copy is locked: "Loaded from rsc_seed_v1 #<id> · Verify
 *    against your tally" per user spec.
 *
 * Props:
 *   label, helper        — passed to <Field>
 *   kind                 — library type (drill_pipe / drill_collar / casing /
 *                          hwdp / mud_motor / rss / mwd / lwd / stabilizer /
 *                          crossover / hole_size / tubing)
 *   pick                 — "od_in" | "id_in"  (which numeric to commit on pick)
 *   value                — current input value
 *   src                  — provenance object {libId, libVer, at} or null
 *   onValueChange(v)     — called when user types (clears provenance)
 *   onPick({value, src}) — called when library item picked
 *   testId               — base test id ("kss-sfv-g-dpOd"), used for picker too
 */
export default function TubularLibraryField({
  label,
  helper,
  kind,
  pick = "od_in",
  value,
  src,
  onValueChange,
  onPick,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const items = useMemo(() => listByKind(kind), [kind]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((it) => (it.label + " " + (it.id || "")).toLowerCase().includes(needle));
  }, [items, q]);

  const handleType = (v) => {
    onValueChange(v);
  };

  const handlePick = (it) => {
    const numeric = it[pick];
    if (numeric == null) return;
    onPick({
      value: String(numeric),
      src: { libId: it.id, libVer: LIB_VERSION, at: new Date().toISOString() },
    });
    setOpen(false);
    setQ("");
  };

  return (
    <Field label={label} helper={helper}>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-stretch gap-2">
          <div className="flex-1">
            <TextInput
              type="number"
              value={value ?? ""}
              onChange={handleType}
              testId={testId}
            />
          </div>
          <button
            type="button"
            data-testid={`${testId}-pick`}
            onClick={() => setOpen((v) => !v)}
            className="text-[11px] px-2.5 rounded-md whitespace-nowrap"
            style={{
              minHeight: 36,
              background: "rgba(22,24,29,0.6)",
              color: "var(--rs-text-2)",
              border: "1px solid var(--rs-border)",
            }}
          >
            {open ? "Close" : "Pick from library"}
          </button>
        </div>

        {/* Provenance pill */}
        {src && src.libId ? (
          <div
            data-testid={`${testId}-provenance`}
            className="text-[10px] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md self-start"
            style={{
              background: "rgba(245,180,0,0.10)",
              color: "var(--rs-amber, #f5b400)",
              border: "1px solid rgba(245,180,0,0.35)",
            }}
          >
            Loaded from {src.libVer || LIB_VERSION} #{src.libId} · Verify against your tally
          </div>
        ) : value !== "" && value != null ? (
          <div
            data-testid={`${testId}-provenance-manual`}
            className="text-[10px] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md self-start"
            style={{
              background: "rgba(255,255,255,0.04)",
              color: "var(--rs-text-3, #6b7785)",
              border: "1px solid var(--rs-border)",
            }}
          >
            Manual override
          </div>
        ) : null}

        {open && (
          <div
            data-testid={`${testId}-picker`}
            className="card-rs p-2 flex flex-col gap-1.5"
            style={{ background: "rgba(10,11,14,0.85)" }}
          >
            <input
              data-testid={`${testId}-picker-search`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search size / grade / id…"
              className="input-rs"
              style={{ minHeight: 32 }}
            />
            <div className="text-[10px] text-[var(--rs-text-3)]">
              {filtered.length} of {items.length} entries · selecting writes <strong>{pick === "od_in" ? "OD" : "ID"}</strong> (in)
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto" }} className="flex flex-col gap-1">
              {filtered.length === 0 ? (
                <div className="text-[11px] text-[var(--rs-text-3)] px-1.5 py-1">
                  No matches. Manual entry remains available.
                </div>
              ) : filtered.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  data-testid={`${testId}-picker-row-${it.id}`}
                  onClick={() => handlePick(it)}
                  className="text-left text-[11px] rounded-md px-2 py-1.5"
                  style={{
                    background: "rgba(22,24,29,0.6)",
                    border: "1px solid var(--rs-border)",
                    color: "var(--rs-text-1)",
                  }}
                >
                  <span style={{ color: "var(--rs-amber, #f5b400)", fontFamily: "JetBrains Mono, monospace" }}>
                    {pick === "od_in" ? (it.od_in ?? "—") : (it.id_in ?? "—")}
                  </span>
                  <span className="text-[var(--rs-text-3)]"> in</span>
                  <span className="text-[var(--rs-text-2)]"> · {it.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Field>
  );
}
